import { useEffect, useMemo, useState } from 'react'
import { apiGet } from '../../../../generic/Api_Functions'

const normalizeApiList = (response) => {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.content)) return response.content
  return []
}

const normalizeRawMaterialList = (response) => {
  const list = Array.isArray(response) ? response : []
  return list.filter((item) => (item?.resourceId ?? item?.id) != null)
}

const buildWorkerLabel = (worker) => {
  const code = String(worker?.code ?? '').trim()
  const name = String(worker?.name ?? '').trim()
  const label = [code, name].filter(Boolean).join(' - ')
  if (label) return label
  if (code) return code
  if (name) return name
  return ''
}

const buildRawMaterialLabel = (resource) => {
  const reference = getRawMaterialReference(resource)
  const code = String(resource?.rawMaterialReferenceCode ?? resource?.resourceReferenceCode ?? reference?.resourceCode ?? reference?.code ?? resource?.resourceCode ?? resource?.code ?? '').trim()
  const name = String(resource?.rawMaterialReferenceName ?? resource?.rawMaterialReferenceDescription ?? resource?.resourceReferenceName ?? reference?.resourceName ?? reference?.name ?? reference?.description ?? resource?.resourceName ?? resource?.name ?? '').trim()
  const label = [code, name].filter(Boolean).join(' · ')
  if (label) return label
  if (code) return code
  if (name) return name
  return ''
}

const getRawMaterialReference = (resource) => (
  resource?.rawMaterialReference ||
  resource?.rawMaterialReferenceDto ||
  null
)

const getRawMaterialReferenceId = (resource) => {
  const reference = getRawMaterialReference(resource)
  return resource?.rawMaterialReferenceId ??
    resource?.rawMaterialReferenceDtoId ??
    reference?.id ??
    reference?.resourceId ??
    resource?.resourceId ??
    resource?.id ??
    null
}

export const useQualityIncidenceDialogData = ({ isOpen, fabricationOrder, node }) => {
  const [rawMaterialsForSelectedJob, setRawMaterialsForSelectedJob] = useState([])
  const [rawMaterialsForWholeOf, setRawMaterialsForWholeOf] = useState([])
  const [workers, setWorkers] = useState([])
  const [isLoadingWorkers, setIsLoadingWorkers] = useState(false)
  const [isLoadingWorkflowResources, setIsLoadingWorkflowResources] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    let cancelled = false

    const loadWorkers = async () => {
      setIsLoadingWorkers(true)
      try {
        const response = await apiGet('allWorkersSimple')
        if (cancelled) return
        setWorkers(normalizeApiList(response))
      } catch (_) {
        if (cancelled) return
        setWorkers([])
      } finally {
        if (!cancelled) {
          setIsLoadingWorkers(false)
        }
      }
    }

    loadWorkers()

    return () => {
      cancelled = true
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    let cancelled = false

    const loadWorkflowResources = async () => {
      const fabricationOrderId = fabricationOrder?.id ?? null
      if (!fabricationOrderId) {
        setRawMaterialsForSelectedJob([])
        setRawMaterialsForWholeOf([])
        return
      }

      setIsLoadingWorkflowResources(true)
      try {
        const [wholeOfResponse, selectedJobResponse] = await Promise.all([
          apiGet(`partWorkflowHistory/rawMaterial/byFoAndWorkflow/${fabricationOrderId}`),
          node?.id
            ? apiGet(`partWorkflowHistory/rawMaterial/byFoAndWorkflow/${fabricationOrderId}`, { nodeId: node.id })
            : Promise.resolve([])
        ])
        if (cancelled) return
        setRawMaterialsForWholeOf(normalizeRawMaterialList(wholeOfResponse))
        setRawMaterialsForSelectedJob(normalizeRawMaterialList(selectedJobResponse))
      } catch (_) {
        if (cancelled) return
        setRawMaterialsForSelectedJob([])
        setRawMaterialsForWholeOf([])
      } finally {
        if (!cancelled) {
          setIsLoadingWorkflowResources(false)
        }
      }
    }

    loadWorkflowResources()

    return () => {
      cancelled = true
    }
  }, [fabricationOrder?.id, isOpen, node?.id])

  const workerOptions = useMemo(() => {
    return workers.map(buildWorkerLabel).filter(Boolean)
  }, [workers])

  const workerByLabel = useMemo(() => {
    const map = new Map()
    workers.forEach((worker) => {
      const label = buildWorkerLabel(worker)
      if (!label) return
      map.set(label, worker)
    })
    return map
  }, [workers])

  const rawMaterialOptionById = useMemo(() => {
    const map = new Map()
    rawMaterialsForWholeOf.forEach((rawMaterial) => {
      const id = getRawMaterialReferenceId(rawMaterial)
      if (id == null) return
      map.set(id, buildRawMaterialLabel(rawMaterial))
    })
    rawMaterialsForSelectedJob.forEach((rawMaterial) => {
      const id = getRawMaterialReferenceId(rawMaterial)
      if (id == null) return
      if (map.has(id)) return
      map.set(id, buildRawMaterialLabel(rawMaterial))
    })
    return map
  }, [rawMaterialsForSelectedJob, rawMaterialsForWholeOf])

  return {
    workerOptions,
    workerByLabel,
    rawMaterialsForSelectedJob,
    rawMaterialsForWholeOf,
    rawMaterialOptionById,
    isLoadingWorkers,
    isLoadingWorkflowResources
  }
}
